/**
 * /projects/new wizard — 2-step flow for creating a project from a template.
 *
 *   1. Pick template (active versions only).
 *   2. Fill project_name + customer_name + calendar_id + the dynamic
 *      `template.parameters` form (any number of date/int/string fields,
 *      including the date-anchor params that drive task scheduling).
 *
 * Submit posts `POST /api/simulate/create_project` — one atomic step that
 * plans and materialises the project in a single backend transaction (the
 * two-phase simulate/commit machinery lives on for future planning flows).
 * On success we navigate to the new /projects/:id; there is no preview step
 * and nothing to apply or reject. Blank and task templates are created with
 * the same single action — no branching on task count.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Combobox } from "@/components/ui/combobox";
import { DateField } from "@/components/ui/date-field";
import { ApiErrorNotice } from "@/components/ui/api-error-notice";
import {
  FormErrorSummary,
  FormFieldError,
  focusValidationControl,
  type FormValidationIssue,
} from "@/components/ui/form-validation";
import {
  EquipmentTypeSection,
  type EquipmentSelectionResult,
} from "@/components/projects/EquipmentTypeSection";
import { presentApiError } from "@/lib/api-error-policy";
import { showApiErrorToast } from "@/lib/api-error-toast";
import { useCalendars, useCreateProject, useTemplates } from "@/hooks/useTemplates";
import { usePutProjectAssemblyElements } from "@/hooks/useEquipment";
import { useUsersLookup } from "@/hooks/useUnits";
import type { Template, TemplateParameter } from "@/api/templates";
import { clearDraft, isEmptyDraft, loadDraft, saveDraft } from "./wizardDraft";
import { useReleaseDirtyState } from "@/lib/releaseDirtyState";

type Step = "template" | "params";

const PROJECT_CODE_CONFLICT_DETAIL = "project code already exists";

interface WizardState {
  step: Step;
  selectedPk: number | null;
  projectName: string;
  code: string;
  customerName: string;
  plannedStart: string;
  plannedEnd: string;
  calendarId: number | null;
  managerUserId: number | null;
  equipmentTypeId: number | null;
  equipmentQuantity: string;
  parameters: Record<string, string>; // raw input values
}

const initialState: WizardState = {
  step: "template",
  selectedPk: null,
  projectName: "",
  code: "",
  customerName: "",
  plannedStart: "",
  plannedEnd: "",
  calendarId: null,
  managerUserId: null,
  equipmentTypeId: null,
  equipmentQuantity: "1",
  parameters: {},
};

export function NewProjectWizard(): React.ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState<WizardState>(() => {
    const draft = loadDraft();
    if (!draft) return initialState;
    return {
      ...initialState,
      step: draft.step,
      selectedPk: draft.selectedPk,
      projectName: draft.projectName,
      code: draft.code,
      customerName: draft.customerName,
      plannedStart: draft.plannedStart,
      plannedEnd: draft.plannedEnd,
      calendarId: draft.calendarId,
      managerUserId: draft.managerUserId,
      equipmentTypeId: draft.equipmentTypeId,
      equipmentQuantity: String(draft.equipmentQuantity),
      parameters: draft.parameters,
    };
  });
  const [issues, setIssues] = useState<FormValidationIssue[]>([]);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [conflictingCode, setConflictingCode] = useState<string | null>(null);
  const [elementIds, setElementIds] = useState<number[]>([]);
  const [allOptionsDecided, setAllOptionsDecided] = useState(true);
  const [hasOptionalDecisions, setHasOptionalDecisions] = useState(false);
  useReleaseDirtyState(hasOptionalDecisions);

  const templatesQuery = useTemplates();
  const calendarsQuery = useCalendars();
  const usersLookup = useUsersLookup({ purpose: "project_manager" });
  const createMut = useCreateProject();
  const putElements = usePutProjectAssemblyElements();

  const activeTemplates = useMemo(
    () => (templatesQuery.data ?? []).filter((tpl) => tpl.status === "active"),
    [templatesQuery.data],
  );

  const selectedTemplate = useMemo(
    () => activeTemplates.find((tpl) => tpl.template_pk === state.selectedPk) ?? null,
    [activeTemplates, state.selectedPk],
  );
  const validationState = useRef({ hasSubmitted, selectedTemplate, state, issues });
  validationState.current = { hasSubmitted, selectedTemplate, state, issues };

  const onEquipmentSelection = useCallback(
    (result: EquipmentSelectionResult) => {
      setElementIds(result.elementIds);
      setAllOptionsDecided(result.allOptionsDecided);
      setHasOptionalDecisions(result.hasOptionalDecisions);
      const current = validationState.current;
      if (current.hasSubmitted && current.selectedTemplate) {
        const serverIssues = current.issues.filter((issue) => issue.kind === "server");
        setIssues([
          ...collectIssues(current.selectedTemplate, current.state, result.allOptionsDecided),
          ...serverIssues,
        ]);
      }
    },
    // EquipmentTypeSection reports from an effect; current form data comes from the ref so this callback stays stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Болваночный режим пилота: пред-выбираем пустой шаблон по умолчанию, как
  // только активные шаблоны загрузились и пользователь ещё ничего не выбрал.
  // Технический id (`blank`) не показываем — матчим по нему молча.
  useEffect(() => {
    if (state.step !== "template" || state.selectedPk !== null) return;
    if (templatesQuery.isLoading || templatesQuery.isError) return;
    const blank = activeTemplates.find((tpl) => tpl.template_id === "blank");
    const blankPk = blank?.template_pk;
    if (blankPk == null) return;
    const defaultCal = calendarsQuery.data?.[0]?.id ?? null;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setState((s) => ({
      ...s,
      selectedPk: blankPk,
      step: "params",
      calendarId: defaultCal,
    }));
  }, [
    state.step,
    state.selectedPk,
    templatesQuery.isLoading,
    templatesQuery.isError,
    activeTemplates,
    calendarsQuery.data,
  ]);

  // Persist draft.
  useEffect(() => {
    const payload = {
      step: state.step,
      selectedPk: state.selectedPk,
      projectName: state.projectName,
      code: state.code,
      customerName: state.customerName,
      plannedStart: state.plannedStart,
      plannedEnd: state.plannedEnd,
      calendarId: state.calendarId,
      managerUserId: state.managerUserId,
      equipmentTypeId: state.equipmentTypeId,
      equipmentQuantity: /^[1-9]\d*$/.test(state.equipmentQuantity)
        ? Number(state.equipmentQuantity)
        : 1,
      parameters: state.parameters,
    };
    if (isEmptyDraft(payload)) {
      clearDraft();
      return;
    }
    saveDraft(payload);
  }, [
    state.step,
    state.selectedPk,
    state.projectName,
    state.code,
    state.customerName,
    state.plannedStart,
    state.plannedEnd,
    state.calendarId,
    state.managerUserId,
    state.equipmentTypeId,
    state.equipmentQuantity,
    state.parameters,
  ]);

  // Если шаблон из черновика больше не существует (удалён / archived) —
  // откатываем wizard на шаг выбора шаблона.
  useEffect(() => {
    if (templatesQuery.isLoading || templatesQuery.isError) return;
    if (state.selectedPk === null) return;
    if (selectedTemplate !== null) return;
    // Server-data sync: шаблон, сохранённый в драфте, исчез из активных —
    // надо откатить wizard на шаг выбора. Прямой setState в effect здесь
    // намеренный (нет другого источника события: templates-fetch завершился).
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setState({ ...initialState });
    setIssues([]);
    setHasSubmitted(false);
  }, [templatesQuery.isLoading, templatesQuery.isError, selectedTemplate, state.selectedPk]);

  function apiErrorPresentation(err: unknown) {
    return presentApiError(err, {
      fallbackMessage: t("common.apiError.fallback"),
      domainRules: [
        {
          code: "project-code-conflict",
          status: 409,
          detail: PROJECT_CODE_CONFLICT_DETAIL,
          message: t("projects.new.errors.codeConflict"),
        },
        {
          code: "simulation-expired",
          status: 410,
          detail: "simulation expired",
          message: t("approvals.toast.expired"),
        },
      ],
    });
  }

  function pickTemplate(tpl: Template): void {
    if (tpl.template_pk === null || tpl.template_pk === undefined) return;
    const defaults: Record<string, string> = {};
    for (const p of tpl.parameters ?? []) {
      if (p.default !== null && p.default !== undefined) {
        defaults[p.name] = String(p.default);
      }
    }
    const defaultCal = calendarsQuery.data?.[0]?.id ?? null;
    setState({
      ...initialState,
      selectedPk: tpl.template_pk,
      step: "params",
      parameters: defaults,
      calendarId: defaultCal,
    });
    setIssues([]);
    setHasSubmitted(false);
  }

  function backToTemplate(): void {
    setState((s) => ({ ...s, step: "template" }));
    setIssues([]);
    setHasSubmitted(false);
  }

  function validateParams(
    tpl: Template,
    values: Record<string, string>,
  ): {
    errors: Record<string, string>;
    parsed: Record<string, string | number>;
  } {
    const errors: Record<string, string> = {};
    const parsed: Record<string, string | number> = {};
    for (const p of tpl.parameters ?? []) {
      const raw = values[p.name];
      const isEmpty = raw === undefined || raw === null || raw === "";
      if (isEmpty) {
        if (p.required) errors[p.name] = t("projects.new.errors.required");
        continue;
      }
      if (p.type === "date") {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
          errors[p.name] = t("projects.new.errors.dateFormat");
        } else {
          parsed[p.name] = raw;
        }
      } else if (p.type === "int") {
        const n = Number(raw);
        if (!Number.isInteger(n)) {
          errors[p.name] = t("projects.new.errors.integer");
        } else {
          parsed[p.name] = n;
        }
      } else {
        parsed[p.name] = raw;
      }
    }
    return { errors, parsed };
  }

  function collectIssues(
    tpl: Template,
    values: WizardState,
    optionsDecided = allOptionsDecided,
  ): FormValidationIssue[] {
    const next: FormValidationIssue[] = [];
    const field = (id: string, message: string, controlId = id.replace(/-error$/, "")): void => {
      next.push({ id, message, kind: "field", controlId });
    };
    if (!values.code.trim())
      field("wizard-field-code-error", t("projects.new.errors.codeRequired"));
    else if (values.code.trim() === conflictingCode) {
      field("wizard-field-code-error", t("projects.new.errors.codeConflict"));
    }
    if (!values.projectName.trim()) {
      field("wizard-field-project-name-error", t("projects.new.errors.projectNameRequired"));
    }
    if (!values.plannedStart) {
      field("wizard-field-start-error", t("projects.new.errors.startRequired"));
    }
    if (!values.plannedEnd) {
      field("wizard-field-end-error", t("projects.new.errors.endRequired"));
    } else if (values.plannedStart && values.plannedEnd < values.plannedStart) {
      field("wizard-field-end-error", t("projects.new.errors.endBeforeStart"));
    }
    if (values.calendarId == null) {
      next.push({
        id: "wizard-calendar-error",
        message: t("projects.new.errors.calendarRequired"),
        kind: "form",
      });
    }
    if (values.managerUserId == null) {
      field("wizard-field-manager-error", t("projects.new.errors.managerRequired"));
    }
    if (values.equipmentTypeId !== null && !/^[1-9]\d*$/.test(values.equipmentQuantity)) {
      field("equipment-quantity-input-error", t("projects.new.errors.positiveInteger"));
    }
    if (values.equipmentTypeId !== null && !optionsDecided) {
      field(
        "equipment-options-error",
        t("projects.equipment.optionUndecidedError"),
        "equipment-options-control",
      );
    }
    const parameterValidation = validateParams(tpl, values.parameters);
    for (const param of tpl.parameters ?? []) {
      const message = parameterValidation.errors[param.name];
      if (message) {
        field(`wizard-param-error-${param.name}`, message, `wizard-param-${param.name}`);
      }
    }
    return next;
  }

  function refreshIssues(nextState: WizardState): void {
    if (!hasSubmitted || !selectedTemplate) return;
    const serverIssues = issues.filter((issue) => issue.kind === "server");
    setIssues([...collectIssues(selectedTemplate, nextState), ...serverIssues]);
  }

  function changeState(patch: Partial<WizardState>): void {
    const nextState = { ...state, ...patch };
    setState(nextState);
    refreshIssues(nextState);
  }

  function changeEquipmentType(equipmentTypeId: number | null): void {
    const nextState = {
      ...state,
      equipmentTypeId,
      ...(equipmentTypeId === null ? { equipmentQuantity: "1" } : {}),
    };
    setState(nextState);
    if (equipmentTypeId === null) {
      setElementIds([]);
      setAllOptionsDecided(true);
      setHasOptionalDecisions(false);
      if (hasSubmitted && selectedTemplate) {
        const serverIssues = issues.filter((issue) => issue.kind === "server");
        setIssues([...collectIssues(selectedTemplate, nextState, true), ...serverIssues]);
      }
      return;
    }
    refreshIssues(nextState);
  }

  function changeParameter(name: string, value: string): void {
    const nextState = { ...state, parameters: { ...state.parameters, [name]: value } };
    setState(nextState);
    refreshIssues(nextState);
  }

  async function submitParams(): Promise<void> {
    if (!selectedTemplate) return;
    const templatePk = selectedTemplate.template_pk;
    if (templatePk === null || templatePk === undefined) return;
    setHasSubmitted(true);
    const nextIssues = collectIssues(selectedTemplate, state);
    setIssues(nextIssues);
    if (nextIssues.length > 0) {
      const firstTarget = nextIssues.find((issue) => issue.kind === "field" && issue.controlId);
      if (firstTarget?.controlId) {
        requestAnimationFrame(() => focusValidationControl(firstTarget.controlId!));
      }
      return;
    }
    const { errors, parsed } = validateParams(selectedTemplate, state.parameters);
    if (Object.keys(errors).length > 0) return;

    try {
      const audit = await createMut.mutateAsync({
        template_pk: templatePk,
        project_name: state.projectName.trim(),
        code: state.code.trim(),
        customer_name: state.customerName.trim(),
        calendar_id: state.calendarId!,
        manager_user_id: state.managerUserId!,
        equipment_type_id: state.equipmentTypeId,
        equipment_quantity: Number(state.equipmentQuantity),
        planned_start: state.plannedStart,
        planned_end: state.plannedEnd,
        parameters: parsed,
        user_comment: "",
      });
      const newId = audit.project_id;
      // Адресный список сохраняем follow-up'ом после создания проекта, чтобы не
      // раздувать атомарный create-simulate (порядок из брифа §3.2).
      if (typeof newId === "number" && state.equipmentTypeId !== null) {
        try {
          await putElements.mutateAsync({ projectId: newId, elementIds });
        } catch (err) {
          showApiErrorToast(err, {
            fallbackMessage: t("projects.equipment.saveElementsFailed"),
          });
        }
      }
      clearDraft();
      toast.success(t("projects.new.applied"));
      if (typeof newId === "number") {
        void navigate({
          to: "/projects/$projectId",
          params: { projectId: String(newId) },
        });
      } else {
        void navigate({ to: "/projects" });
      }
    } catch (err) {
      const presentation = apiErrorPresentation(err);
      if (presentation.kind === "domain" && presentation.code === "project-code-conflict") {
        setConflictingCode(state.code.trim());
        setIssues([
          {
            id: "wizard-field-code-error",
            message: t("projects.new.errors.codeConflict"),
            kind: "field",
            controlId: "wizard-field-code",
          },
        ]);
        requestAnimationFrame(() => focusValidationControl("wizard-field-code"));
        return;
      }
      setIssues([
        {
          id: "wizard-create-error",
          message: "message" in presentation ? presentation.message : t("common.apiError.fallback"),
          kind: "server",
          requestId: presentation.requestId,
        },
      ]);
    }
  }

  return (
    <div className="flex flex-col gap-4" data-testid="new-project-wizard">
      <Stepper currentStep={state.step} />

      {state.step === "template" && (
        <TemplateStep
          isLoading={templatesQuery.isLoading}
          isError={templatesQuery.isError}
          error={templatesQuery.error}
          templates={activeTemplates}
          onPick={pickTemplate}
          onRetry={() => void templatesQuery.refetch()}
        />
      )}

      {state.step === "params" && selectedTemplate && (
        <ParamsStep
          template={selectedTemplate}
          users={(usersLookup.data ?? []).map((user) => ({
            id: user.id,
            name: user.display_name,
          }))}
          state={state}
          issues={issues}
          onChange={changeState}
          onEquipmentTypeChange={changeEquipmentType}
          onParameterChange={changeParameter}
          onEquipmentSelection={onEquipmentSelection}
          onBack={backToTemplate}
          onSubmit={() => void submitParams()}
          isSubmitting={createMut.isPending || putElements.isPending}
        />
      )}
    </div>
  );
}

const STEPS: Step[] = ["template", "params"];

function Stepper({ currentStep }: { currentStep: Step }): React.ReactElement {
  const { t } = useTranslation();
  const idx = STEPS.indexOf(currentStep);
  return (
    <ol className="flex items-center gap-2 text-sm" data-testid="wizard-stepper">
      {STEPS.map((s, i) => {
        const active = s === currentStep;
        const done = i < idx;
        return (
          <li
            key={s}
            data-testid={`wizard-step-${s}`}
            data-active={active ? "true" : undefined}
            data-done={done ? "true" : undefined}
            className={[
              "flex items-center gap-2 rounded px-3 py-1",
              active
                ? "bg-primary text-primary-foreground"
                : done
                  ? "tone-success"
                  : "bg-muted text-muted-foreground",
            ].join(" ")}
          >
            <span className="font-mono text-xs">{i + 1}</span>
            <span>{t(`projects.new.steps.${s}`)}</span>
          </li>
        );
      })}
    </ol>
  );
}

interface TemplateStepProps {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  templates: Template[];
  onPick: (tpl: Template) => void;
  onRetry: () => void;
}

function TemplateStep({
  isLoading,
  isError,
  error,
  templates,
  onPick,
  onRetry,
}: TemplateStepProps): React.ReactElement {
  const { t } = useTranslation();
  if (isLoading) {
    return <p className="text-muted-foreground py-6">{t("common.loading")}</p>;
  }
  if (isError) {
    return (
      <p className="py-6" data-testid="new-project-templates-error">
        <ApiErrorNotice
          className="text-destructive"
          error={error}
          fallbackMessage={t("common.apiError.fallback")}
        />
        <Button variant="ghost" size="sm" className="ml-2" onClick={onRetry}>
          {t("common.retry")}
        </Button>
      </p>
    );
  }
  if (templates.length === 0) {
    return (
      <p className="text-muted-foreground py-6 text-sm" data-testid="new-project-no-templates">
        {t("projects.new.noTemplates")}
      </p>
    );
  }
  return (
    <div className="grid gap-3 sm:grid-cols-2" data-testid="wizard-template-list">
      {templates.map((tpl) => (
        <button
          key={tpl.template_pk}
          type="button"
          data-testid={`wizard-template-${tpl.template_pk}`}
          className="border-border hover:bg-accent flex flex-col items-start gap-1 rounded-[var(--radius-card)] border p-4 text-left"
          onClick={() => onPick(tpl)}
        >
          <span className="font-medium">{tpl.name}</span>
          <span className="text-muted-foreground text-xs">{`v${tpl.version}`}</span>
          {tpl.description && (
            <span className="text-muted-foreground text-xs">{tpl.description}</span>
          )}
          <span className="text-muted-foreground mt-1 text-xs">
            {t("projects.new.parametersCount", { count: tpl.parameters?.length ?? 0 })}
          </span>
        </button>
      ))}
    </div>
  );
}

interface ParamsStepProps {
  template: Template;
  users: { id: number; name: string }[];
  state: WizardState;
  issues: FormValidationIssue[];
  onChange: (patch: Partial<WizardState>) => void;
  onEquipmentTypeChange: (equipmentTypeId: number | null) => void;
  onParameterChange: (name: string, value: string) => void;
  onEquipmentSelection: (result: EquipmentSelectionResult) => void;
  onBack: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}

function ParamsStep({
  template,
  users,
  state,
  issues,
  onChange,
  onEquipmentTypeChange,
  onParameterChange,
  onEquipmentSelection,
  onBack,
  onSubmit,
  isSubmitting,
}: ParamsStepProps): React.ReactElement {
  const { t } = useTranslation();
  const issue = (id: string): FormValidationIssue | undefined =>
    issues.find((candidate) => candidate.id === id);
  return (
    <form
      data-testid="wizard-params-form"
      className="flex flex-col gap-4"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <header className="flex items-baseline justify-between">
        <div>
          <h2 className="text-base font-semibold">{template.name}</h2>
          <p className="text-muted-foreground text-xs">{`v${template.version}`}</p>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={onBack}>
          <ChevronLeft className="h-4 w-4" />
          {t("projects.new.back")}
        </Button>
      </header>

      <Field
        label={t("projects.new.fields.code")}
        required
        error={issue("wizard-field-code-error")}
      >
        <Input
          id="wizard-field-code"
          data-testid="wizard-field-code"
          className="font-mono"
          value={state.code}
          onChange={(e) => onChange({ code: e.target.value })}
          required
          aria-invalid={Boolean(issue("wizard-field-code-error"))}
          aria-describedby={issue("wizard-field-code-error")?.id}
        />
      </Field>
      <Field
        label={t("projects.new.fields.projectName")}
        required
        error={issue("wizard-field-project-name-error")}
      >
        <Input
          id="wizard-field-project-name"
          data-testid="wizard-field-project-name"
          value={state.projectName}
          onChange={(e) => onChange({ projectName: e.target.value })}
          required
          aria-invalid={Boolean(issue("wizard-field-project-name-error"))}
          aria-describedby={issue("wizard-field-project-name-error")?.id}
        />
      </Field>
      <Field label={t("projects.new.fields.customerName")}>
        <Input
          data-testid="wizard-field-customer-name"
          value={state.customerName}
          onChange={(e) => onChange({ customerName: e.target.value })}
        />
      </Field>
      <Field
        label={t("projects.new.fields.start")}
        required
        error={issue("wizard-field-start-error")}
      >
        <DateField
          id="wizard-field-start"
          data-testid="wizard-field-start"
          value={state.plannedStart}
          onChange={(value) => onChange({ plannedStart: value ?? "" })}
          label={t("projects.new.fields.start")}
          required
          aria-invalid={Boolean(issue("wizard-field-start-error"))}
          aria-describedby={issue("wizard-field-start-error")?.id}
        />
      </Field>
      <Field label={t("projects.new.fields.end")} required error={issue("wizard-field-end-error")}>
        <DateField
          id="wizard-field-end"
          data-testid="wizard-field-end"
          value={state.plannedEnd}
          onChange={(value) => onChange({ plannedEnd: value ?? "" })}
          label={t("projects.new.fields.end")}
          min={state.plannedStart || undefined}
          required
          aria-invalid={Boolean(issue("wizard-field-end-error"))}
          aria-describedby={issue("wizard-field-end-error")?.id}
        />
      </Field>
      <Field
        label={t("projects.new.fields.manager")}
        required
        error={issue("wizard-field-manager-error")}
      >
        <Combobox
          id="wizard-field-manager"
          data-testid="wizard-field-manager"
          options={users.map((u) => ({ value: String(u.id), label: u.name }))}
          value={state.managerUserId != null ? String(state.managerUserId) : null}
          onChange={(v) => onChange({ managerUserId: v === null ? null : Number(v) })}
          placeholder={t("projects.new.fields.managerPlaceholder")}
          searchPlaceholder={t("common.combobox.searchPlaceholder")}
          emptyText={t("common.combobox.empty")}
          aria-invalid={Boolean(issue("wizard-field-manager-error"))}
          aria-describedby={issue("wizard-field-manager-error")?.id}
        />
      </Field>

      <fieldset className="border-border flex flex-col gap-3 rounded-[var(--radius-card)] border p-3">
        <legend className="text-muted-foreground px-1 text-xs uppercase">
          {t("projects.equipment.sectionLegend")}
        </legend>
        <EquipmentTypeSection
          typeId={state.equipmentTypeId}
          quantity={state.equipmentQuantity}
          onTypeChange={onEquipmentTypeChange}
          onQuantityChange={(equipmentQuantity) => onChange({ equipmentQuantity })}
          onSelectionChange={onEquipmentSelection}
          quantityError={issue("equipment-quantity-input-error")}
          optionsError={issue("equipment-options-error")}
        />
      </fieldset>

      {(template.parameters ?? []).length > 0 && (
        <fieldset className="border-border flex flex-col gap-3 rounded-[var(--radius-card)] border p-3">
          <legend className="text-muted-foreground px-1 text-xs uppercase">
            {t("projects.new.parametersLegend")}
          </legend>
          {(template.parameters ?? []).map((param) => (
            <ParamField
              key={param.name}
              param={param}
              value={state.parameters[param.name] ?? ""}
              error={issue(`wizard-param-error-${param.name}`)}
              onChange={(v) => onParameterChange(param.name, v)}
            />
          ))}
        </fieldset>
      )}

      <FormErrorSummary
        issues={issues}
        countLabel={(count) => t("validation.fieldErrorCount", { count })}
        data-testid="wizard-error-summary"
      />

      <footer className="flex justify-end gap-2" data-testid="wizard-actions">
        <Button type="button" variant="outline" onClick={onBack}>
          {t("projects.new.back")}
        </Button>
        <Button type="submit" disabled={isSubmitting} data-testid="wizard-submit-params">
          {isSubmitting ? t("projects.new.creating") : t("projects.new.create")}
          <ChevronRight className="h-4 w-4" />
        </Button>
      </footer>
    </form>
  );
}

interface FieldProps {
  label: string;
  required?: boolean;
  children: React.ReactNode;
  error?: FormValidationIssue;
}

function Field({ label, required, children, error }: FieldProps): React.ReactElement {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-medium">
        {label}
        {required ? <span className="text-destructive">{" *"}</span> : null}
      </span>
      {children}
      <FormFieldError issue={error} />
    </label>
  );
}

interface ParamFieldProps {
  param: TemplateParameter;
  value: string;
  error: FormValidationIssue | undefined;
  onChange: (value: string) => void;
}

function ParamField({ param, value, error, onChange }: ParamFieldProps): React.ReactElement {
  return (
    <div className="flex flex-col gap-1 text-sm">
      <label className="font-medium" htmlFor={`wizard-param-${param.name}`}>
        {param.name}
        {param.required ? <span className="text-destructive">{" *"}</span> : null}
      </label>
      {param.description && (
        <span className="text-muted-foreground text-xs">{param.description}</span>
      )}
      {param.type === "date" ? (
        <DateField
          id={`wizard-param-${param.name}`}
          data-testid={`wizard-param-${param.name}`}
          value={value || null}
          onChange={(next) => onChange(next ?? "")}
          label={param.name}
          required={param.required}
          aria-invalid={Boolean(error)}
          aria-describedby={error?.id}
        />
      ) : (
        <Input
          id={`wizard-param-${param.name}`}
          data-testid={`wizard-param-${param.name}`}
          type={param.type === "int" ? "number" : "text"}
          step={param.type === "int" ? 1 : undefined}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={param.required}
          aria-invalid={Boolean(error)}
          aria-describedby={error?.id}
        />
      )}
      <FormFieldError issue={error} />
    </div>
  );
}
